import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/auth-context.jsx';
import { isTenantRestrictedModeActiveError } from '../../lib/admin-api/http-client.js';
import { getRuntimeConfig } from '../../lib/runtime-config.js';
import { BrandedMark, normalizeBranding } from '../../shared/branding/branding.jsx';

function readTokenFromHash() {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  return String(params.get('token') || '').trim();
}

function clearHashToken() {
  if (!window.location.hash) {
    return;
  }

  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}`,
  );
}

function SupportAccessLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { consumeSupportAccess } = useAuth();
  const branding = normalizeBranding(getRuntimeConfig().branding);
  const submittedTokenRef = useRef(null);
  const [error, setError] = useState('');
  const [isRestrictedMode, setIsRestrictedMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = readTokenFromHash();

    if (!token) {
      if (submittedTokenRef.current) {
        return;
      }

      setError(t('auth.supportAccessMissingLink'));
      setIsLoading(false);
      return;
    }

    if (submittedTokenRef.current === token) {
      return;
    }

    submittedTokenRef.current = token;
    clearHashToken();
    setError('');
    setIsRestrictedMode(false);
    setIsLoading(true);

    consumeSupportAccess(token)
      .then(() => {
        navigate('/', { replace: true });
      })
      .catch((requestError) => {
        if (isTenantRestrictedModeActiveError(requestError)) {
          setIsRestrictedMode(true);
          setError(t('auth.restrictedModeNotice'));
        } else {
          setError(requestError.message || t('auth.supportAccessUseFailed'));
        }
        setIsLoading(false);
      });
  }, [consumeSupportAccess, navigate, t]);

  return (
    <div id="main-wrapper" className="auth-customizer-none">
      <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100 d-flex align-items-center justify-content-center">
        <div className="d-flex align-items-center justify-content-center w-100">
          <div className="row justify-content-center w-100">
            <div className="col-md-8 col-lg-6 col-xxl-3 auth-card">
              <div className="card mb-0 position-relative">
                <div className="card-body">
                  <div className="text-nowrap logo-img d-flex align-items-center justify-content-center mb-4 w-100">
                    <BrandedMark branding={branding} imageClassName="crm-brand-logo-auth" imageHeight={52} />
                  </div>

                  <h5 className="mb-1">{t('auth.supportAccessTitle')}</h5>
                  <p className="mb-4 text-muted">{t('auth.supportAccessSubtitle')}</p>

                  {isLoading ? (
                    <div className="d-flex align-items-center justify-content-center py-4">
                      <div className="spinner-border text-theme" role="status" aria-label={t('auth.supportAccessSigningIn')}></div>
                    </div>
                  ) : null}

                  {error ? (
                    <>
                      <div className={`alert ${isRestrictedMode ? 'alert-warning' : 'alert-danger'} py-2`} role="alert">{error}</div>
                      <div className="d-flex align-items-center justify-content-center">
                        <Link to="/login" className="text-primary fw-medium">
                          {t('auth.supportAccessBackToSignIn')}
                        </Link>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportAccessLoginPage;
